import { Box3, BufferAttribute, BufferGeometry, Frustum, Matrix4, Mesh, Sphere, Vector3, type Camera, type Material, type Object3D } from "three";
import { CHUNK_SIZE, CHUNKS_PER_SIDE } from "@eldrun/worldgen";
import { LOD_COUNT, lodIndices, lodVertexCount, skirtDepth } from "./chunk-mesh.ts";
import { distanceToChunk, selectLod } from "./lod.ts";
import { QUALITY } from "../render/quality.ts";
import type { ResponseOf } from "./protocol.ts";
import { chunkKey, chunkOrigin } from "./tile.ts";
import { CancelledError } from "./worker-pool.ts";

/** O que o gerenciador precisa do pool de workers (permite testar com uma fonte falsa). */
export interface MeshSource {
  mesh(cx: number, cz: number, lod: number, priority: number): Promise<ResponseOf<"mesh">>;
  cancelMesh(cx: number, cz: number, lod: number): boolean;
  updatePriority(cx: number, cz: number, lod: number, priority: number): void;
}

interface ChunkState {
  cx: number;
  cz: number;
  minY: number;
  maxY: number;
  /** Chunks inteiramente submersos e fundos ficam sempre no LOD mais grosso. */
  deep: boolean;
  mesh: Mesh;
  lod: number | undefined;
  requested: number | undefined;
  /** Identifica o pedido atual: respostas e falhas de pedidos antigos são ignoradas. */
  generation: number;
  failures: number;
  /** Desenhado dentro da malha do bloco (a malha própria fica escondida). */
  merged: boolean;
}

/** Lado do bloco em chunks (F36): 4 × 4 chunks = 512 m. */
export const BLOCK_CHUNKS = 4;
/** LODs a partir deste se juntam por bloco; o mais fino (perto, muda toda hora) fica por chunk. */
export const MERGE_FROM_LOD = 1;

/** Uma malha por bloco e por LOD com os chunks daquele LOD (F36: ~170 draw calls do relevo viram ~40). */
interface BlockMesh {
  mesh: Mesh;
  /** Chunks e geometrias de que foi feita: muda, refaz. */
  signature: string;
  members: ChunkState[];
}

/** Falhas seguidas toleradas por chunk antes de desistir (e avisar). */
export const MAX_MESH_FAILURES = 3;

/** Orçamento de upload por quadro (doc 04): 2 malhas L0 ou 6 dos demais LODs. */
const UPLOAD_BUDGET = 6;
const uploadCost = (lod: number): number => (lod === 0 ? 3 : 1);

const indexAttributes = Array.from({ length: LOD_COUNT }, (_, lod) => new BufferAttribute(lodIndices(lod), 1));

/** Pool de geometrias por LOD: atributos pré-alocados, atualizados no lugar (sem realocar na GPU). */
class GeometryPool {
  private readonly free: BufferGeometry[][] = Array.from({ length: LOD_COUNT }, () => []);
  created = 0;

  acquire(lod: number): BufferGeometry {
    const geometry = this.free[lod]?.pop();
    if (geometry) return geometry;
    this.created++;
    const g = new BufferGeometry();
    const count = lodVertexCount(lod);
    g.setAttribute("position", new BufferAttribute(new Float32Array(count * 3), 3));
    g.setAttribute("color", new BufferAttribute(new Uint8Array(count * 4), 4, true));
    g.setAttribute("normal", new BufferAttribute(new Int8Array(count * 4), 4, true));
    g.setIndex(indexAttributes[lod] as BufferAttribute);
    g.userData["lod"] = lod;
    return g;
  }

  release(geometry: BufferGeometry): void {
    this.free[geometry.userData["lod"] as number]?.push(geometry);
  }

  dispose(): void {
    for (const list of this.free) for (const g of list) g.dispose();
    for (const list of this.free) list.length = 0;
  }
}

export interface ChunkBounds {
  minY: number;
  maxY: number;
}

export interface TerrainStats {
  chunksByLod: number[];
  triangles: number;
  pending: number;
  geometries: number;
}

/**
 * Escolhe o LOD de cada chunk pela distância da câmera, pede malhas ao pool de workers por
 * prioridade (visível e perto primeiro) e troca a geometria quando a nova chega, mantendo a
 * antiga visível até lá. O culling por frustum fica com o Three (bounding sphere por chunk).
 */
export class TerrainManager {
  readonly root: Object3D;
  private readonly chunks: ChunkState[] = [];
  private readonly arrivals: { result: ResponseOf<"mesh">; generation: number }[] = [];
  private readonly geometries = new GeometryPool();
  private readonly frustum = new Frustum();
  private readonly projScreen = new Matrix4();
  private readonly sphere = new Sphere();
  private readonly pool: MeshSource;
  private readonly material: Material;
  /** Malhas juntadas, por bloco e LOD (`${bx},${bz},${lod}`). */
  private readonly blocks = new Map<string, BlockMesh>();
  /** Última falha definitiva de malha (exibida pela cena). */
  failure: Error | undefined;

  constructor(root: Object3D, material: Material, pool: MeshSource, bounds: ReadonlyMap<number, ChunkBounds>) {
    this.root = root;
    this.pool = pool;
    this.material = material;
    for (let cz = 0; cz < CHUNKS_PER_SIDE; cz++) {
      for (let cx = 0; cx < CHUNKS_PER_SIDE; cx++) {
        const b = bounds.get(chunkKey(cx, cz));
        if (!b) throw new Error(`chunk ${cx},${cz} sem limites`);
        const mesh = new Mesh(new BufferGeometry(), material);
        // Fase 13: o chão não projeta na sombra do sol (a luz suave já desenha o relevo; a sombra do
        // relevo em si custaria o terreno inteiro no passe da luz).
        mesh.userData["noShadow"] = true;
        mesh.matrixAutoUpdate = false;
        mesh.visible = false;
        mesh.name = `chunk-${cx}-${cz}`;
        root.add(mesh);
        this.chunks.push({
          cx,
          cz,
          minY: b.minY,
          maxY: b.maxY,
          deep: b.maxY < -3,
          mesh,
          lod: undefined,
          requested: undefined,
          generation: 0,
          failures: 0,
          merged: false,
        });
      }
    }
  }

  update(camera: Camera & { position: Vector3 }): void {
    this.projScreen.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.projScreen);
    const cameraX = camera.position.x;
    const cameraZ = camera.position.z;
    for (const chunk of this.chunks) {
      if (chunk.failures >= MAX_MESH_FAILURES) continue;
      const distance = distanceToChunk(cameraX, cameraZ, chunk.cx, chunk.cz);
      const current = chunk.requested ?? chunk.lod;
      // Distância de visão das Opções (F30): faixas de LOD mais perto ou mais longe.
      const want = selectLod(distance / QUALITY.distance, current, chunk.deep);
      const visible = this.inFrustum(chunk);
      const priority = distance + (visible ? 0 : 600) + (chunk.lod === undefined ? 0 : 200);
      if (want === chunk.lod && chunk.requested === undefined) continue;
      if (want === chunk.requested) {
        this.pool.updatePriority(chunk.cx, chunk.cz, want, priority);
        continue;
      }
      if (chunk.requested !== undefined) this.pool.cancelMesh(chunk.cx, chunk.cz, chunk.requested);
      if (want === chunk.lod) {
        chunk.requested = undefined;
        continue;
      }
      chunk.requested = want;
      const generation = ++chunk.generation;
      this.pool.mesh(chunk.cx, chunk.cz, want, priority).then(
        (result) => this.arrivals.push({ result, generation }),
        (error: unknown) => {
          if (error instanceof CancelledError || chunk.generation !== generation) return;
          // Libera o pedido para ser refeito no próximo quadro (até MAX_MESH_FAILURES vezes).
          chunk.requested = undefined;
          chunk.failures++;
          if (chunk.failures >= MAX_MESH_FAILURES) {
            this.failure = error instanceof Error ? error : new Error(String(error));
            console.error(`[terreno] desisti do chunk ${chunk.cx},${chunk.cz}`, error);
          }
        },
      );
    }
    this.applyArrivals();
    this.mergeBlocks();
  }

  /**
   * Junta, em cada bloco, os chunks do mesmo LOD (a partir de `MERGE_FROM_LOD`) já prontos e sem
   * pedido pendente numa malha só. Quem pede outro LOD sai do bloco na hora e volta a ser desenhado
   * pela própria malha (que continua com a geometria antiga até a nova chegar). Refaz só o bloco
   * cuja composição mudou.
   */
  private mergeBlocks(): void {
    const groups = new Map<string, ChunkState[]>();
    for (const chunk of this.chunks) {
      if (chunk.lod === undefined || chunk.lod < MERGE_FROM_LOD || chunk.requested !== undefined || !chunk.mesh.visible && !chunk.merged) continue;
      const key = `${Math.floor(chunk.cx / BLOCK_CHUNKS)},${Math.floor(chunk.cz / BLOCK_CHUNKS)},${chunk.lod}`;
      let list = groups.get(key);
      if (!list) groups.set(key, (list = []));
      list.push(chunk);
    }
    // Blocos que deixaram de existir ou mudaram: devolve os chunks às malhas próprias.
    for (const [key, block] of this.blocks) {
      const members = groups.get(key);
      const signature = members && members.length > 1 ? members.map((c) => `${c.cx}:${c.cz}:${c.mesh.geometry.id}`).join("|") : "";
      if (signature === block.signature) continue;
      this.unmerge(key, block);
    }
    for (const [key, members] of groups) {
      if (members.length < 2 || this.blocks.has(key)) continue;
      const mesh = new Mesh(mergeGeometries(members.map((c) => c.mesh.geometry)), this.material);
      mesh.userData["noShadow"] = true;
      mesh.matrixAutoUpdate = false;
      mesh.name = `bloco-${key.replace(/,/g, "-")}`;
      this.root.add(mesh);
      for (const c of members) {
        c.merged = true;
        c.mesh.visible = false;
      }
      this.blocks.set(key, { mesh, members, signature: members.map((c) => `${c.cx}:${c.cz}:${c.mesh.geometry.id}`).join("|") });
    }
  }

  private unmerge(key: string, block: BlockMesh): void {
    this.root.remove(block.mesh);
    block.mesh.geometry.dispose();
    for (const c of block.members) {
      if (!c.merged) continue;
      c.merged = false;
      c.mesh.visible = c.lod !== undefined;
    }
    this.blocks.delete(key);
  }

  /** Malhas desenhadas pelo relevo (chunks soltos e blocos). */
  get meshes(): number {
    return this.chunks.filter((c) => c.mesh.visible).length + this.blocks.size;
  }

  get stats(): TerrainStats {
    const chunksByLod = new Array<number>(LOD_COUNT).fill(0);
    let triangles = 0;
    for (const c of this.chunks) {
      if (c.lod === undefined) continue;
      chunksByLod[c.lod] = (chunksByLod[c.lod] as number) + 1;
      if (c.mesh.visible || c.merged) triangles += lodIndices(c.lod).length / 3;
    }
    return { chunksByLod, triangles, pending: this.chunks.filter((c) => c.requested !== undefined).length, geometries: this.geometries.created };
  }

  /** Libera geometrias na GPU e remove os chunks da cena (idempotente). */
  dispose(): void {
    for (const [key, block] of this.blocks) this.unmerge(key, block);
    for (const chunk of this.chunks) {
      this.root.remove(chunk.mesh);
      chunk.mesh.geometry.dispose();
    }
    this.chunks.length = 0;
    this.arrivals.length = 0;
    this.geometries.dispose();
  }

  /** Todos os chunks já têm alguma malha? */
  get complete(): boolean {
    return this.chunks.length > 0 && this.chunks.every((c) => c.lod !== undefined);
  }

  private inFrustum(chunk: ChunkState): boolean {
    const x = chunkOrigin(chunk.cx) + CHUNK_SIZE / 2;
    const z = chunkOrigin(chunk.cz) + CHUNK_SIZE / 2;
    const y = (chunk.minY + chunk.maxY) / 2;
    const halfY = (chunk.maxY - chunk.minY) / 2 + 8;
    this.sphere.center.set(x, y, z);
    this.sphere.radius = Math.sqrt(2 * (CHUNK_SIZE / 2) ** 2 + halfY * halfY);
    return this.frustum.intersectsSphere(this.sphere);
  }

  private applyArrivals(): void {
    let budget = UPLOAD_BUDGET;
    while (this.arrivals.length > 0) {
      const next = this.arrivals[0] as { result: ResponseOf<"mesh">; generation: number };
      const chunk = this.chunks[chunkKey(next.result.cx, next.result.cz)] as ChunkState;
      if (chunk.generation !== next.generation || chunk.requested !== next.result.lod) {
        this.arrivals.shift(); // pedido já superado por outro LOD
        continue;
      }
      const cost = uploadCost(next.result.lod);
      if (cost > budget) break; // fica para o próximo quadro
      this.arrivals.shift();
      budget -= cost;
      const result = next.result;
      const geometry = this.geometries.acquire(result.lod);
      const position = geometry.getAttribute("position") as BufferAttribute;
      const color = geometry.getAttribute("color") as BufferAttribute;
      (position.array as Float32Array).set(result.positions);
      (color.array as Uint8Array).set(result.colors);
      const normal = geometry.getAttribute("normal") as BufferAttribute;
      (normal.array as Int8Array).set(result.normals);
      position.needsUpdate = true;
      color.needsUpdate = true;
      normal.needsUpdate = true;
      const x0 = chunkOrigin(chunk.cx);
      const z0 = chunkOrigin(chunk.cz);
      geometry.boundingBox = new Box3(
        new Vector3(x0, chunk.minY - skirtDepth(result.lod), z0),
        new Vector3(x0 + CHUNK_SIZE, chunk.maxY, z0 + CHUNK_SIZE),
      );
      geometry.boundingSphere = geometry.boundingBox.getBoundingSphere(new Sphere());
      const previous = chunk.mesh.geometry;
      chunk.mesh.geometry = geometry;
      if (previous.userData["lod"] !== undefined) this.geometries.release(previous);
      else previous.dispose();
      // Geometria nova: o bloco (se fazia parte) será refeito pela assinatura; até lá, a malha própria.
      chunk.mesh.visible = true;
      chunk.merged = false;
      chunk.lod = result.lod;
      chunk.requested = undefined;
      chunk.failures = 0;
    }
  }
}

/**
 * Junta geometrias de chunks (posições já no mundo, cores RGBA 8 bits) numa só, com os índices
 * deslocados. Cabe em `Uint16` até o LOD 1 (16 chunks × 1.217 vértices); por garantia, `Uint32` acima.
 */
export function mergeGeometries(parts: readonly BufferGeometry[]): BufferGeometry {
  let vertices = 0;
  let indices = 0;
  for (const g of parts) {
    vertices += g.getAttribute("position").count;
    indices += g.getIndex()?.count ?? 0;
  }
  const positions = new Float32Array(vertices * 3);
  const colors = new Uint8Array(vertices * 4);
  const normals = new Int8Array(vertices * 4);
  const index = vertices > 65535 ? new Uint32Array(indices) : new Uint16Array(indices);
  const box = new Box3();
  let v = 0;
  let i = 0;
  for (const g of parts) {
    const p = g.getAttribute("position") as BufferAttribute;
    const c = g.getAttribute("color") as BufferAttribute;
    positions.set(p.array as Float32Array, v * 3);
    colors.set(c.array as Uint8Array, v * 4);
    const n = g.getAttribute("normal") as BufferAttribute | undefined;
    if (n) normals.set(n.array as Int8Array, v * 4);
    const src = g.getIndex()?.array ?? [];
    for (let k = 0; k < src.length; k++) index[i + k] = (src[k] as number) + v;
    if (g.boundingBox) box.union(g.boundingBox);
    v += p.count;
    i += src.length;
  }
  const merged = new BufferGeometry();
  merged.setAttribute("position", new BufferAttribute(positions, 3));
  merged.setAttribute("color", new BufferAttribute(colors, 4, true));
  merged.setAttribute("normal", new BufferAttribute(normals, 4, true));
  merged.setIndex(new BufferAttribute(index, 1));
  merged.boundingBox = box;
  merged.boundingSphere = box.getBoundingSphere(new Sphere());
  return merged;
}
